import React, { useMemo, useCallback, useState } from 'react';
import { useSvgStore, downloadSvgContent, ensurePreviewableSvg } from '../../store/svgStore';
import SvgPreview from '../common/SvgPreview';
import { SvgNodeElement } from '../../types/svgTypes';
import ConfirmModal from '../common/ConfirmModal';
import { showToast } from '../common/Toast';

// 定义模拟数据接口
interface MockAsset {
  id: string;
  name: string;
  svg_code: string;
  is_public: boolean;
  from_ai_prompt?: string;
}

const SvgAssetLibrary: React.FC = () => {
  const assets = useSvgStore((state) => state.assets);
  const canvas = useSvgStore((state) => state.canvas);
  const viewport = useSvgStore((state) => state.viewport);
  const addElement = useSvgStore((state) => state.addElement);
  const deleteAsset = useSvgStore((state) => state.deleteAsset);

  // ★ 新增：删除确认弹窗状态
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const displayAssets = useMemo(() => {
    if (!Array.isArray(assets)) return [];
    // 过滤掉 svg_code 或 name 为空的无效数据
    return assets.filter((a) => a.id && a.svg_code);
  }, [assets]);

  /** ★ 修复：添加到画布的当前可视区域中心 */
  const handleAddAssetToCanvas = useCallback((assetId: string, svgCode: string, name?: string) => {
    if (!canvas) return;

    // ★ 计算当前可视区域中心点
    const visibleCenterX = viewport.panX + (canvas.width / viewport.zoom) / 2;
    const visibleCenterY = viewport.panY + (canvas.height / viewport.zoom) / 2;

    const element: SvgNodeElement = {
      id: `asset_${assetId}_${Date.now()}`,
      type: 'svg',
      sourceType: 'asset',
      assetId,
      svgContent: svgCode,
      originalSvgContent: svgCode,
      x: Math.round(visibleCenterX - 30),
      y: Math.round(visibleCenterY - 30),
      scale: 1,
      rotation: 0,
      opacity: 1,
      name: name || 'Asset Node',
    };
    addElement(element);
  }, [canvas, viewport, addElement]);

  /** ★ 新增：导出素材 SVG */
  const handleExportAsset = useCallback((e: React.MouseEvent, name: string, svgCode: string) => {
    e.stopPropagation();
    downloadSvgContent(svgCode, `${name || 'asset'}.svg`);
  }, []);

  /** ★ 新增：删除素材 */
  const handleDeleteAsset = useCallback((e: React.MouseEvent, assetId: string, assetName: string) => {
    e.stopPropagation();
    setDeleteTarget({ id: assetId, name: assetName });
  }, [deleteAsset]);

  /** 确认删除 */
  const handleConfirmDelete = useCallback(() => {
    if (!deleteTarget) return;
    deleteAsset(deleteTarget.id);
    showToast(`「${deleteTarget.name}」已删除`, 'info');
    setDeleteTarget(null);
  }, [deleteTarget, deleteAsset]);

  /** 取消删除 */
  const handleCancelDelete = useCallback(() => {
    setDeleteTarget(null);
  }, []);

  return (
    <div className="ai-svg-asset-library-light">
      <div className="ai-svg-asset-grid-light">
        {displayAssets.map((asset) => (
          <div
            key={asset.id}
            className="ai-svg-asset-card-light"
          >
            {/* 预览区域 - 点击添加到画布 */}
            <div
              className="ai-svg-asset-preview-light"
              onClick={() => handleAddAssetToCanvas(asset.id, asset.svg_code, asset.name)}
              title={`点击添加到画布: ${asset.name}`}
            >
              {/* ★ 修复：使用 dangerouslySetInnerHTML 直接渲染，确保片段也能显示 */}
              <div
                className="ai-svg-asset-preview-inner"
                dangerouslySetInnerHTML={{ __html: ensurePreviewableSvg(asset.svg_code) }}
              />

              {/* ★ 悬浮操作按钮组 */}
              <div className="ai-svg-asset-hover-actions">
                {/* 导出按钮 */}
                <span
                  className="ai-svg-asset-action-btn ai-svg-asset-export-btn"
                  onClick={(e) => handleExportAsset(e, asset.name, asset.svg_code)}
                  title="导出 SVG"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                </span>
                {/* ★ 删除按钮 */}
                <span
                  className="ai-svg-asset-action-btn ai-svg-asset-delete-btn"
                  onClick={(e) => handleDeleteAsset(e, asset.id, asset.name)}
                  title="删除素材"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                    <path d="M10 11v6" />
                    <path d="M14 11v6" />
                  </svg>
                </span>
              </div>
            </div>
            <div className="ai-svg-asset-name-light">{asset.name}</div>
          </div>
        ))}
        {displayAssets.length === 0 && (
          <div className="ai-svg-empty-state-light">
            暂无素材，可以从 AI 生成结果中保存
          </div>
        )}
      </div>

      {/* ★ 删除确认弹窗 */}
      <ConfirmModal
        visible={deleteTarget !== null}
        title="删除素材"
        message={`确定要删除「${deleteTarget?.name ?? ''}」吗？删除后无法恢复。`}
        confirmText="删除"
        cancelText="取消"
        danger
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};

export default SvgAssetLibrary;